import { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Input,
  Button,
  Empty,
  Spin,
  Space,
  Typography,
  Form,
  ColorPicker,
  message,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import {
  listAgentCategories,
  addAgentCategory,
  type AgentCategoryRow,
} from '@/api/lookups';
import { trackEvent } from '@/lib/telemetry';
import { colors } from '@/theme/tokens';
import { useStore } from '@/stores';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// AgentCategoryPickerDrawer
//
// Drawer-based picker that replaces the prior inline Select. Two reasons:
//   1. Category lists grow over time (operators add new ones); a Drawer
//      with a real search field scales much better than a cramped popup
//      Select.
//   2. Project convention (CLAUDE.md): "prefer drawers over modals for most
//      interactive flows" — list pickers + multi-step authoring belong in
//      drawers so the underlying page stays visible for context.
//
// Two views inside the same Drawer:
//   • LIST view  — search input + filtered list of categories. Click a row
//                  to commit (single click; no separate Save button).
//   • CREATE view — slides over the LIST when the user taps "+ Add new".
//                  Has Name (required) + Description + Color. Submitting
//                  creates the category, refreshes the list, and immediately
//                  commits the new category to the agent before closing.
//
// All write paths go through the parent's `onSelect` callback so the
// drawer stays purely presentational. The parent (AgentCategoryEditor)
// is responsible for calling updateAgent + applyAgentPatch.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  /** Currently-selected category id, used to highlight the active row. */
  currentCategoryId?: number;
  /**
   * Called when the user picks a category. `null` is fired when the user
   * picks the optional "Any category" / clear row (only rendered when
   * `clearLabel` is set — typically the filter use-case).
   *
   * The parent is responsible for the actual write (updateAgent on the
   * editor side, store update on the filter side) and any optimistic UI.
   * This drawer handles only the picker UX.
   */
  onSelect: (category: AgentCategoryRow | null) => void | Promise<void>;
  /**
   * When set, the picker hides the create affordance and the inline-create
   * form entirely. Rarely wanted — prefer `assignOnCreate={false}` for
   * read-only contexts, so operators can still add a category without the
   * creation implying a selection.
   */
  hideCreate?: boolean;
  /**
   * Whether creating a category should immediately commit it via `onSelect`.
   *
   * True (default) suits the AGENT EDITOR: creating a category there is an
   * unambiguous statement of intent — "put this agent in the new bucket" —
   * so assigning it saves a redundant second click.
   *
   * False suits the FILTER: a category created seconds ago has no agents in
   * it yet, so auto-applying it as the filter would empty the list and read
   * as a broken screen. Instead the drawer returns to the list with the new
   * category visible, and the operator chooses when to filter by it.
   */
  assignOnCreate?: boolean;
  /**
   * When set, an extra "clear" row renders at the top of the list
   * (e.g. "Any category" for filters). Clicking it fires
   * `onSelect(null)`.
   */
  clearLabel?: string;
}

export function AgentCategoryPickerDrawer({
  open,
  onClose,
  currentCategoryId,
  onSelect,
  hideCreate,
  assignOnCreate = true,
  clearLabel,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // ── Lookup ────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState<AgentCategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const refresh = async (): Promise<AgentCategoryRow[]> => {
    setLoading(true);
    try {
      const rows = await listAgentCategories();
      setCategories(rows);
      return rows;
    } catch {
      return categories;
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Search ────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const filtered = useMemo<AgentCategoryRow[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q),
    );
  }, [categories, search]);

  // ── View toggle ───────────────────────────────────────────────────────
  const [view, setView] = useState<'list' | 'create'>('list');
  // Reset view to 'list' whenever the drawer reopens — a previous "create"
  // session should not persist if the user closes + reopens.
  useEffect(() => {
    if (open) {
      setView('list');
      setSearch('');
    }
  }, [open]);

  // ── Create form ───────────────────────────────────────────────────────
  const [form] = Form.useForm<{
    name: string;
    description?: string;
    color?: string;
  }>();
  const [creating, setCreating] = useState(false);

  // Per-row "picking" state so the user gets immediate feedback the moment
  // they click. Without this, `onSelect` was fire-and-forget (`void onSelect`)
  // and the drawer stayed inert while the parent's API call ran — if the
  // parent silently 404'd, the user saw "nothing happens" with no clue why.
  // `pickingId` doubles as the disabled-flag for sibling rows: once a pick
  // is in flight we lock the rest of the list so the user can't fire two
  // mutations into the wrong UI.
  const [pickingId, setPickingId] = useState<number | 'clear' | null>(null);

  const handlePick = async (
    row: AgentCategoryRow | null,
    key: number | 'clear',
  ): Promise<void> => {
    if (pickingId !== null) return; // dedupe rapid double-clicks
    setPickingId(key);
    try {
      trackEvent(row ? 'AgentCategory.Picked' : 'AgentCategory.Cleared', {
        id: row?.id ?? null,
      });
      await onSelect(row);
    } catch (err) {
      // Defensive: most parents already handle their own errors with a
      // toast, but if a parent forgets, the drawer surfaces it rather
      // than swallowing silently.
      message.error(
        err instanceof Error ? err.message : 'Failed to update category',
      );
    } finally {
      setPickingId(null);
    }
  };

  const startCreate = (): void => {
    form.resetFields();
    // Pre-fill the name with whatever the user has been searching for — saves
    // a keystroke in the exact case that sends people here: the search came up
    // empty and they decided "fine, I'll add it".
    if (search.trim()) form.setFieldValue('name', search.trim());
    setView('create');
  };

  const handleCreate = async (): Promise<void> => {
    const values = await form.validateFields();
    setCreating(true);
    try {
      const created = await addAgentCategory({
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        color: values.color || undefined,
        isActive: true,
      });
      await refresh();
      trackEvent('AgentCategory.Created', {
        id: created.id,
        name: created.name,
        assigned: assignOnCreate,
      });

      if (assignOnCreate) {
        // Editor context: creating the category IS the statement of intent,
        // so commit it and let the parent close the drawer.
        await onSelect(created);
        form.resetFields();
      } else {
        // Filter context: the new category has no agents yet, so applying it
        // as the filter would blank the list. Go back to the list — now
        // containing it — and leave the choice to the operator.
        form.resetFields();
        setSearch('');
        setView('list');
        message.success(`“${created.name}” added. It has no agents yet.`);
      }
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setCreating(false);
    }
  };

  // ── Theme-aware tokens ────────────────────────────────────────────────
  const headerBg = isDark ? colors.slate[800] : '#FFFFFF';
  const sectionBg = isDark ? colors.slate[900] : colors.slate[50];
  const borderColor = isDark ? colors.slate[700] : colors.slate[200];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={520}
      closable={false}
      title={null}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column' },
        header: { display: 'none' },
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px 14px',
          borderBottom: `1px solid ${borderColor}`,
          background: headerBg,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {view === 'create' ? (
              <Button
                size="small"
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => setView('list')}
                aria-label="Back to category list"
              />
            ) : (
              <AppstoreOutlined style={{ fontSize: 17, color: colors.ink[600] }} />
            )}
            <Text strong style={{ fontSize: 16 }}>
              {view === 'list' ? 'Pick a category' : 'New category'}
            </Text>
          </div>
          {/* The create affordance lives in the FOOTER, next to Cancel, where
              a drawer's actions belong — see startCreate(). */}
        </div>
        {view === 'list' && (
          <div style={{ marginTop: 10 }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search categories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              autoFocus
            />
          </div>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          background: sectionBg,
        }}
      >
        {view === 'list' ? (
          loading && categories.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : filtered.length === 0 && !clearLabel ? (
            <div style={{ padding: 40 }}>
              <Empty
                description={
                  search.trim()
                    ? `No categories match "${search.trim()}"`
                    : 'No categories yet'
                }
              />
            </div>
          ) : (
            <>
              {/* Optional "clear" row — only rendered in filter mode
                  (clearLabel set). Always present at top, regardless of
                  search; users can quickly clear the filter even when
                  searching for a specific category. */}
              {clearLabel && (
                <div
                  style={{
                    padding: '14px 22px',
                    borderBottom: `1px solid ${borderColor}`,
                    background: !currentCategoryId
                      ? isDark
                        ? colors.slate[800]
                        : '#EFF6FF'
                      : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {/* Whole-row click stays as a convenience target — but the
                      explicit Select button on the right is the unambiguous
                      affordance. The button keeps working even when the row's
                      div click is swallowed by overlapping focus / mask
                      layering on stacked drawers. */}
                  <div
                    onClick={() => { void handlePick(null, 'clear'); }}
                    style={{ flex: 1, cursor: pickingId !== null ? 'default' : 'pointer' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text strong>{clearLabel}</Text>
                      {!currentCategoryId && (
                        <CheckCircleFilled
                          style={{ color: colors.ink[500], fontSize: 14 }}
                          aria-label="Currently selected"
                        />
                      )}
                    </div>
                    <Paragraph
                      type="secondary"
                      style={{ margin: '2px 0 0', fontSize: 12 }}
                    >
                      Show every agent regardless of category.
                    </Paragraph>
                  </div>
                  <Button
                    size="small"
                    type={!currentCategoryId ? 'default' : 'primary'}
                    ghost={!currentCategoryId ? false : true}
                    loading={pickingId === 'clear'}
                    disabled={pickingId !== null && pickingId !== 'clear'}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handlePick(null, 'clear');
                    }}
                  >
                    {!currentCategoryId ? 'Selected' : 'Select'}
                  </Button>
                </div>
              )}
              {filtered.length === 0 ? (
                <div style={{ padding: 40 }}>
                  <Empty
                    description={
                      search.trim()
                        ? `No categories match "${search.trim()}"`
                        : 'No categories yet'
                    }
                  />
                </div>
              ) : (
                // Plain divs instead of antd List.Item — List.Item's
                // onClick was being swallowed inside the stacked
                // drawer-on-drawer mask layering on the Filter screen
                // (clicks would hover-highlight but never fire). Manual
                // divs match the pattern the "clear" row above already
                // uses and gives us a single, predictable click target.
                <div role="listbox" aria-label="Categories">
                  {filtered.map((c) => {
                    const isActive = c.id === currentCategoryId;
                    const isThisRowPicking = pickingId === c.id;
                    const isAnotherRowPicking = pickingId !== null && !isThisRowPicking;
                    return (
                      <div
                        key={c.id}
                        role="option"
                        aria-selected={isActive}
                        style={{
                          padding: '14px 22px',
                          borderBottom: `1px solid ${borderColor}`,
                          background: isActive
                            ? isDark
                              ? colors.slate[800]
                              : '#EFF6FF'
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          opacity: isAnotherRowPicking ? 0.5 : 1,
                        }}
                      >
                        {/* Row body — also clickable as a convenience target,
                            but the explicit Select button on the right is
                            the unambiguous affordance the user requested.
                            With both, a slow / silently-failing parent
                            commit no longer reads as "nothing happens" —
                            the button shows a loading spinner while the
                            await resolves. */}
                        <div
                          onClick={() => {
                            if (pickingId !== null) return;
                            void handlePick(c, c.id);
                          }}
                          style={{
                            flex: 1,
                            cursor: pickingId !== null ? 'default' : 'pointer',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong>{c.name}</Text>
                            {isActive && (
                              <CheckCircleFilled
                                style={{ color: colors.ink[500], fontSize: 14 }}
                                aria-label="Currently selected"
                              />
                            )}
                          </div>
                          {c.description && (
                            <Paragraph
                              type="secondary"
                              ellipsis={{ rows: 2 }}
                              style={{ margin: '2px 0 0', fontSize: 12 }}
                            >
                              {c.description}
                            </Paragraph>
                          )}
                        </div>
                        <Button
                          size="small"
                          type={isActive ? 'default' : 'primary'}
                          ghost={isActive ? false : true}
                          loading={isThisRowPicking}
                          disabled={isAnotherRowPicking || isActive}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handlePick(c, c.id);
                          }}
                          aria-label={isActive ? 'Already selected' : `Select ${c.name}`}
                        >
                          {isActive ? 'Selected' : 'Select'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )
        ) : (
          <div style={{ padding: '20px 22px' }}>
            <Form form={form} layout="vertical" requiredMark="optional" preserve={false}>
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  { required: true, message: 'Name is required' },
                  { max: 200, message: 'Max 200 characters' },
                ]}
              >
                <Input autoFocus placeholder="e.g. Cardiology" maxLength={200} />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ max: 1000, message: 'Max 1000 characters' }]}
              >
                <Input.TextArea
                  rows={3}
                  placeholder="What kinds of agents belong in this bucket?"
                  maxLength={1000}
                  showCount
                />
              </Form.Item>
              {/* Label carries no "(optional)" — the Form's requiredMark="optional"
                  already appends one, and hard-coding it rendered "(optional) (optional)". */}
              <Form.Item label="Accent color" name="color">
                <ColorPicker
                  format="hex"
                  onChangeComplete={(c) => form.setFieldValue('color', c.toHexString())}
                />
              </Form.Item>
            </Form>
            <Divider style={{ margin: '8px 0 16px' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {assignOnCreate
                ? "The new category will be assigned to this agent immediately after it's created — one click, no extra confirm."
                : 'The category is added to the list for everyone. Nothing is filtered yet — a new category has no agents in it, so picking it here would empty the list.'}
            </Text>
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '12px 20px',
          borderTop: `1px solid ${borderColor}`,
          background: headerBg,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {view === 'list' ? (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {filtered.length === categories.length
                ? `${categories.length} categor${categories.length === 1 ? 'y' : 'ies'}`
                : `${filtered.length} of ${categories.length} match`}
            </Text>
            <Space>
              {!hideCreate && (
                <Button icon={<PlusOutlined />} onClick={startCreate}>
                  New category
                </Button>
              )}
              <Button onClick={onClose}>Cancel</Button>
            </Space>
          </>
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Esc to go back to the list
            </Text>
            <Space>
              <Button onClick={() => setView('list')} disabled={creating}>
                Back
              </Button>
              <Button
                type="primary"
                loading={creating}
                onClick={() => void handleCreate()}
              >
                {assignOnCreate ? 'Create & assign' : 'Create category'}
              </Button>
            </Space>
          </>
        )}
      </div>
    </Drawer>
  );
}
